import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './SubAccountList.css';

/**
 * 子账户列表组件
 * 
 * @param {Object} props
 * @param {String} props.courtId - 场地ID（必填）
 * @param {Function} props.onRefresh - 刷新回调
 * @param {Function} props.onAccountClick - 账户点击回调
 */
const SubAccountList = forwardRef(({ courtId, onRefresh, onAccountClick }, ref) => {
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [adjustFormData, setAdjustFormData] = useState({
    adjustmentAmount: '',
    description: ''
  });

  const baseUrl = getApiBaseUrl();

  // 获取子账户列表
  const fetchSubAccounts = async () => {
    if (!courtId) {
      setError('请先选择场地');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${baseUrl}/api/court-usage/sub-accounts`, {
        params: { courtId, isActive: true }
      });
      if (response.data.success) {
        setSubAccounts(response.data.data || []);
      } else {
        throw new Error(response.data.message || '获取子账户列表失败');
      }
    } catch (err) {
      console.error('获取子账户列表失败:', err);
      setError(err.response?.data?.message || err.message || '获取子账户列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courtId) {
      fetchSubAccounts();
    } else {
      setSubAccounts([]);
      setLoading(false);
    }
  }, [courtId]);

  // 使用 useImperativeHandle 暴露刷新方法给父组件
  useImperativeHandle(ref, () => ({
    refresh: fetchSubAccounts
  }));

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    fetchSubAccounts();
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleAccountClick = (account) => {
    if (onAccountClick) {
      onAccountClick(account);
    }
  };

  // 打开调整场时表单
  const handleOpenAdjustForm = (account, e) => {
    e?.stopPropagation(); // 阻止事件冒泡，避免触发账户点击
    setSelectedAccount(account);
    setAdjustFormData({
      adjustmentAmount: '',
      description: ''
    });
    setShowAdjustForm(true);
    setError(null);
  };

  // 关闭调整场时表单
  const handleCloseAdjustForm = () => {
    setShowAdjustForm(false);
    setSelectedAccount(null);
    setAdjustFormData({
      adjustmentAmount: '',
      description: ''
    });
  };

  // 提交调整场时
  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    
    if (!adjustFormData.adjustmentAmount || adjustFormData.adjustmentAmount === '0') {
      setError('调整数量不能为空或0');
      return;
    }

    const adjustmentValue = parseFloat(adjustFormData.adjustmentAmount);
    if (isNaN(adjustmentValue) || adjustmentValue === 0) {
      setError('调整数量不能为0');
      return;
    }

    try {
      setError(null);
      const response = await axios.post(
        `${baseUrl}/api/court-usage/sub-accounts/${selectedAccount._id}/adjust`,
        {
          adjustmentAmount: adjustmentValue,
          description: adjustFormData.description.trim() || (adjustmentValue > 0 ? '场时调整（增加）' : '场时调整（减少）')
        }
      );

      if (response.data.success) {
        await fetchSubAccounts();
        handleCloseAdjustForm();
        if (onRefresh) {
          onRefresh();
        }
      } else {
        throw new Error(response.data.message || '调整失败');
      }
    } catch (err) {
      console.error('调整场时失败:', err);
      setError(err.response?.data?.message || err.message || '调整失败');
    }
  };

  if (loading) {
    return (
      <div className="sub-account-list">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sub-account-list">
      <div className="sub-account-header">
        <h2>子账户列表</h2>
        <button 
          className="create-button"
          onClick={() => setShowCreateForm(true)}
        >
          + 创建子账户
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {showCreateForm && (
        <SubAccountForm
          courtId={courtId}
          onClose={() => setShowCreateForm(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* 调整场时表单模态框 */}
      {showAdjustForm && selectedAccount && (
        <div className="sub-account-form-overlay" onClick={handleCloseAdjustForm}>
          <div className="sub-account-form-container" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h3>调整场时 - {selectedAccount.name}</h3>
              <button className="close-button" onClick={handleCloseAdjustForm}>×</button>
            </div>
            <form onSubmit={handleAdjustSubmit} className="sub-account-form">
              {error && (
                <div className="error-message">{error}</div>
              )}
              
              <div className="form-group">
                <label htmlFor="adjustmentAmount">
                  调整数量（场时） *
                  <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                    （正数表示增加，负数表示减少，例如：+2.5 或 -1.5）
                  </span>
                </label>
                <input
                  type="number"
                  id="adjustmentAmount"
                  name="adjustmentAmount"
                  value={adjustFormData.adjustmentAmount}
                  onChange={(e) => {
                    setAdjustFormData({ ...adjustFormData, adjustmentAmount: e.target.value });
                    setError('');
                  }}
                  placeholder="例如：+2.5 或 -1.5"
                  step="0.01"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="adjustDescription">调整说明</label>
                <textarea
                  id="adjustDescription"
                  name="description"
                  value={adjustFormData.description}
                  onChange={(e) => setAdjustFormData({ ...adjustFormData, description: e.target.value })}
                  placeholder="可选，填写调整原因或说明"
                  rows="3"
                />
              </div>

              <div className="form-group" style={{ 
                padding: '10px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                fontSize: '14px',
                color: '#666'
              }}>
                <div>当前余额: <strong>{selectedAccount.currentBalance.toFixed(2)}</strong> 场时</div>
                {adjustFormData.adjustmentAmount && !isNaN(parseFloat(adjustFormData.adjustmentAmount)) && (
                  <div style={{ marginTop: '5px' }}>
                    调整后余额: <strong>
                      {(selectedAccount.currentBalance + parseFloat(adjustFormData.adjustmentAmount)).toFixed(2)}
                    </strong> 场时
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={handleCloseAdjustForm}
                >
                  取消
                </button>
                <button 
                  type="submit" 
                  className="submit-button"
                >
                  确认调整
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {subAccounts.length === 0 ? (
        <div className="empty-state">
          <p>暂无子账户</p>
          <button 
            className="primary-button"
            onClick={() => setShowCreateForm(true)}
          >
            创建第一个子账户
          </button>
        </div>
      ) : (
        <div className="sub-account-grid">
          {subAccounts.map(account => (
            <div 
              key={account._id} 
              className={`sub-account-card ${account.currentBalance > 0 ? 'positive-balance' : account.currentBalance < 0 ? 'negative-balance' : ''}`}
              onClick={() => handleAccountClick(account)}
            >
              <div className="sub-account-card-content">
                <div className="sub-account-name">{account.name}</div>
                <div className={`sub-account-balance ${account.currentBalance < 0 ? 'negative' : ''}`}>
                  余额: {account.currentBalance.toFixed(2)} 场时
                </div>
                {account.description && (
                  <div className="sub-account-description">{account.description}</div>
                )}
              </div>
              <button
                className="adjust-button"
                onClick={(e) => handleOpenAdjustForm(account, e)}
                title="调整场时（可增加或减少）"
              >
                🔧 调整场时
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * 子账户创建表单组件
 */
const SubAccountForm = ({ courtId, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    initialBalance: '0'
  });
  const [error, setError] = useState('');

  const baseUrl = getApiBaseUrl();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!courtId) {
      setError('场地ID不能为空');
      return;
    }
    
    if (!formData.name || !formData.name.trim()) {
      setError('账户名称不能为空');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const requestData = {
        name: formData.name.trim(),
        description: formData.description || '',
        initialBalance: parseFloat(formData.initialBalance) || 0,
        courtId: courtId
      };

      const response = await axios.post(`${baseUrl}/api/court-usage/sub-accounts`, requestData);
      
      if (response.data.success) {
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response.data.message || '创建失败');
      }
    } catch (err) {
      console.error('创建子账户失败:', err);
      setError(err.response?.data?.message || err.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sub-account-form-overlay">
      <div className="sub-account-form-container">
        <div className="form-header">
          <h3>创建子账户</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="sub-account-form">
          {error && (
            <div className="error-message">{error}</div>
          )}

          <div className="form-group">
            <label htmlFor="name">账户名称 *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="如：毛亮、刘鹏/森军"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">账户说明</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="可选，填写账户说明"
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="initialBalance">初始余额</label>
            <input
              type="number"
              id="initialBalance"
              name="initialBalance"
              value={formData.initialBalance}
              onChange={handleChange}
              placeholder="0"
              step="0.01"
            />
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              取消
            </button>
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {loading ? '创建中...' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

SubAccountList.displayName = 'SubAccountList';

export default SubAccountList;

